import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bed } from "@phosphor-icons/react";
import { mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import useRecommendations from "@/hooks/useRecommendations";
import useStayCities from "@/hooks/useStayCities";
import { responsiveImage } from "@/lib/images";
import { pick } from "@/lib/languages";
import { defaultTripDates, otherStayCities, staysLink, worldCards } from "@/lib/worldDestinations";
import { homeL } from "@/i18n/home";
import { worldL } from "@/i18n/world";
import HomeSection from "@/components/home/HomeSection";
import WorldDestinations from "@/components/explore/WorldDestinations";

const SECTIONS = ["popular", "weekend", "budget"];

/**
 * The real dated fares the recommendations found (a round trip from the
 * visitor's detected origin), by destination airport; the first section that
 * has a city wins. Empty while loading, when the service is down or when it
 * found none.
 */
export function faresByCode(reco) {
  const fares = new Map();
  if (!reco || reco.loading || reco.failed) return fares;
  for (const id of SECTIONS) {
    for (const card of reco.section(id).items || []) {
      const code = String(card?.destination?.code || "").trim().toUpperCase();
      if (code && !fares.has(code)) fares.set(code, { card, section: id });
    }
  }
  return fares;
}

/**
 * Home's "Explore the world": the world destinations as photo cards
 * (components/explore/WorldDestinations), on every continent and always
 * there, each opening a flight search from the visitor's detected airport on
 * the search form's own dates (leaving tomorrow for a week: the airlines
 * are only asked for a dated search, and the band says so).
 * A city with a real dated fare shows it ("Flights from X", its dates) and
 * comes first; a city Toukky lists stays in links them with how many. The
 * other cities Toukky lists stays in follow as links with one of their own
 * photos. No invented price, count or popularity.
 */
export default function HomeDestinations() {
  const { lang, siteLang = lang } = useSettings();
  const l = pick(homeL, siteLang).destinations;
  const w = pick(worldL, lang);
  const reco = useRecommendations({ remember: false });
  const cities = useStayCities();
  // Fixed for the visit, like the form's own defaults.
  const [dates] = useState(defaultTripDates);
  const fares = faresByCode(reco);
  const origin = reco.origin || {};
  const withFares = worldCards({ fares }).some((card) => card.fare);
  const others = otherStayCities(cities);
  return (
    <HomeSection id="destinations" title={l.title} note={withFares && origin.city ? w.faresNote(origin.city) : w.sub}
      data-testid="home-destinations">
      <WorldDestinations origin={origin.code || ""} fares={fares} stays={cities} dates={dates} />
      {others.length ? <StayCities cities={others} title={l.moreStays} w={w} /> : null}
    </HomeSection>
  );
}

/** The other cities Toukky lists stays in: each a link to them, with how many and a photo of one. */
function StayCities({ cities, title, w }) {
  return (
    <div className="mt-6 sm:mt-8" data-testid="home-stay-cities">
      <h3 className="text-label-lg font-semibold text-touki-black">{title}</h3>
      {/* One row that scrolls sideways on a phone, like the cards above; it wraps from 640px. */}
      <ul className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 py-1 no-scrollbar sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
        {cities.map((entry) => {
          const photo = entry.image ? responsiveImage(mediaUrl(entry.image), 40, "40px") : null;
          return (
            <li key={entry.city} className="shrink-0 sm:shrink sm:min-w-0">
              <Link to={staysLink(entry.city)} data-testid={`explore-${entry.city}`}
                className="tk-zoom inline-flex min-h-11 max-w-full items-center gap-2.5 rounded-pill border border-touki bg-white py-1 pe-4 ps-1 text-touki-black transition-colors duration-fast hover:border-touki-field">
                <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-touki-light text-touki-muted">
                  {photo ? (
                    <img {...photo} alt="" width={40} height={40} loading="lazy" decoding="async" className="tk-zoom-photo h-full w-full object-cover" />
                  ) : <Bed size={16} weight="bold" aria-hidden="true" />}
                </span>
                <span className="min-w-0 truncate text-label-lg font-semibold">{entry.city}</span>{" "}
                {/* No number when the list read was not the whole inventory (hooks/useStayCities). */}
                {entry.count == null ? null : (
                  <span className="shrink-0 text-label-md text-touki-muted tabular-nums" data-testid="destination-stays">{w.stays(entry.count)}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
